import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Target, Eye, Compass } from "lucide-react";

/**
 * Mission, Vision and Values — single-page brief intended for inclusion in
 * the CIO governing document and supporting submissions to the Charity
 * Commission for England and Wales.
 */

const VALUES = [
  { word: "Prajñā", english: "Wisdom", body: "Learning is the foundation of every ABGN-UK activity. We teach, we read, we question, we share knowledge freely." },
  { word: "Karuṇā", english: "Compassion", body: "Care for others is the test of our practice. We support members, especially the isolated, the new and the vulnerable." },
  { word: "Sama-tā", english: "Equality", body: "Every member is treated as an equal. Caste, gender, age and background do not stratify the Sangha." },
  { word: "Mettā", english: "Loving-kindness", body: "We engage with the broader world, including those who disagree with us, in a spirit of warmth and goodwill." },
  { word: "Vīrya", english: "Energy", body: "We act. We do not let the Sangha exist only on paper. Volunteers, events and outputs are real and ongoing." },
];

export default function MissionVision() {
  return (
    <div className="bg-pearl">
      <section className="bg-gradient-to-b from-navy via-navy to-navy-light text-pearl">
        <div className="section-container py-16 sm:py-20">
          <p className="eyebrow text-saffron">Mission · Vision · Values</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-4xl">
            The compass that guides every gathering.
          </h1>
        </div>
      </section>

      <section className="py-16">
        <div className="section-container max-w-4xl space-y-12">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="bg-white border-l-4 border-saffron p-7 rounded-sm shadow-sm">
            <div className="inline-flex items-center gap-2 text-saffron">
              <Target size={18} />
              <p className="eyebrow text-saffron mb-0">Mission</p>
            </div>
            <p className="mt-4 font-display text-2xl sm:text-3xl text-navy leading-snug">
              To advance the education of the public in the Buddha's Dhamma, in the
              tradition embraced by Babasaheb Dr. B. R. Ambedkar, and to support the
              wellbeing, equality and inclusion of all who wish to learn or practise
              alongside us in the United Kingdom and beyond.
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="bg-white border-l-4 border-gold p-7 rounded-sm shadow-sm">
            <div className="inline-flex items-center gap-2 text-gold">
              <Eye size={18} />
              <p className="eyebrow text-gold mb-0">Vision</p>
            </div>
            <p className="mt-4 font-display text-2xl sm:text-3xl text-navy leading-snug">
              A just, peaceful and enlightened society, together in Dhamma — where the
              teachings of the Buddha and the social conscience of Babasaheb Ambedkar
              are accessible to every age, every language and every corner of British
              life.
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="bg-white border-l-4 border-navy p-7 rounded-sm shadow-sm">
            <div className="inline-flex items-center gap-2 text-navy">
              <Compass size={18} />
              <p className="eyebrow mb-0">Values</p>
            </div>
            <p className="mt-4 text-slate-700 leading-relaxed">
              Five Buddhist principles shape how we work. They are not slogans; they are tested in every meeting, decision and policy.
            </p>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {VALUES.map((v, i) => (
                <div key={v.word} className="border border-navy/10 rounded-sm p-5 bg-pearl" data-testid={`mv-value-${i}`}>
                  <div className="flex items-baseline gap-2">
                    <span className="font-display text-xl text-saffron">{v.word}</span>
                    <span className="text-[11px] uppercase tracking-widest text-navy/55">{v.english}</span>
                  </div>
                  <p className="mt-2 text-sm text-slate-700 leading-relaxed">{v.body}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <div className="text-center pt-8">
            <p className="text-slate-700 mb-6">
              These statements are reviewed annually by the volunteer governance committee.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/public-benefit" className="px-5 py-2.5 bg-saffron/15 border border-saffron/40 text-saffron rounded-sm text-sm hover:bg-saffron/25" data-testid="mv-pb-link">
                Public benefit statement
              </Link>
              <Link to="/about" className="px-5 py-2.5 bg-navy text-pearl rounded-sm text-sm hover:bg-navy-light" data-testid="mv-about-link">
                About ABGN-UK
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
